import type {
  OrgEventAutomationCondition,
  OrgEventAutomationSource,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  feedPresetFromSource,
  sourceForPreset,
  type FeedPreset,
} from './automationRuleLogic';
import { FEED_OPTIONS, applyPresetToCondition } from './ruleFormModel';
import {
  catalogSourceDefaults,
  findWeatherLayerByUrl,
  normalizeWeatherServiceUrl,
  weatherLayersByCategory,
} from './weatherFeatureServerCatalog';

import {
  DEFAULT_FEED_UNITS,
  OWM_GROUP_LABEL,
  OWM_SOURCE_NAME,
  STATIC_EXTERNAL_FEEDS,
  buildFeedUri,
  convertFeedValue,
  feedDisplayName,
  feedFields,
  parseFeedUri,
  withFeedUnits,
  type ExternalFeedDef,
  type FeedUnits,
} from './externalFeeds';

export const BUILT_IN_FEEDS_GROUP_LABEL = 'Built-in feeds';

const PRESET_KEY_PREFIX = 'preset:';
const FEED_KEY_PREFIX = 'feed:';

export type DataFeedPick =
  | { kind: 'preset'; preset: FeedPreset }
  | { kind: 'url'; serviceUrl: string }
  /**
   * A non-ArcGIS feed URI; units are kept from the condition when it already
   * reads that feed.
   */
  | { kind: 'feed'; uri: string };

export type DataFeedOption = {
  /**
   * `preset:<preset>` for built-in feeds, `feed:<provider>/<product>` for
   * non-ArcGIS feeds, the service URL for catalog layers.
   */
  key: string;
  name: string;
  url: string;
  /** Readable host and service, e.g. "noaa.gov · Hazards / Wpc Precip Hazards". */
  source: string;
  pick: DataFeedPick;
};

function titleCaseWords(value: string): string {
  return value
    .replace(/_v\d+$/i, '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** "arcgis.com · Active Hurricanes" from an ArcGIS REST layer URL. */
export function readableFeedSource(url: string): string {
  const feedName = feedDisplayName(url);
  if (feedName) {
    const units = parseFeedUri(url)?.units;
    return units ? `${feedName} · ${units}` : feedName;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const host = parsed.hostname.split('.').slice(-2).join('.');
  const parts = decodeURIComponent(parsed.pathname).split('/').filter(Boolean);
  const servicesAt = parts.findIndex((part) => part === 'services');
  const serverAt = parts.findIndex((part) =>
    /^(Feature|Map)Server$/.test(part)
  );
  const service =
    servicesAt >= 0 && serverAt > servicesAt
      ? parts
          .slice(servicesAt + 1, serverAt)
          .map(titleCaseWords)
          .join(' / ')
      : '';
  return [host, service].filter(Boolean).join(' · ');
}

/** Matches a typed query against an option's name, readable source, or URL. */
export function dataFeedOptionMatches(
  option: DataFeedOption,
  query: string
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [option.name, option.source, decodeURIComponent(option.url)].some(
    (text) => text.toLowerCase().includes(needle)
  );
}

export type DataFeedGroup = { label: string; items: DataFeedOption[] };

/**
 * One list for the Data feed control: the built-in feeds first, then every
 * catalog layer by category. Catalog layers that a built-in feed already reads
 * are left out so each URL appears once.
 */
export function dataFeedGroups(
  feeds: ExternalFeedDef[] = STATIC_EXTERNAL_FEEDS
): DataFeedGroup[] {
  const builtIns: DataFeedOption[] = FEED_OPTIONS.map((option) => {
    const preset = option.value as FeedPreset;
    const url = sourceForPreset(preset).serviceUrl ?? '';
    return {
      key: `${PRESET_KEY_PREFIX}${preset}`,
      name: option.label,
      url,
      source: readableFeedSource(url),
      pick: { kind: 'preset', preset },
    };
  });
  const builtInUrls = new Set(
    builtIns.map((option) => normalizeWeatherServiceUrl(option.url))
  );
  const catalogGroups = weatherLayersByCategory({ pinAnalytics: true })
    .map((group) => ({
      label: group.label,
      items: group.items
        .filter(
          (url) =>
            !builtInUrls.has(normalizeWeatherServiceUrl(url)) &&
            !findWeatherLayerByUrl(url)?.hiddenFromPickers
        )
        .map(
          (url): DataFeedOption => ({
            key: url,
            name: findWeatherLayerByUrl(url)?.name ?? url,
            url,
            source: readableFeedSource(url),
            pick: { kind: 'url', serviceUrl: url },
          })
        ),
    }))
    .filter((group) => group.items.length > 0);
  const feedItems = feeds.map((feed): DataFeedOption => {
    const uri = buildFeedUri({
      provider: feed.provider,
      product: feed.product,
      units: DEFAULT_FEED_UNITS,
    });
    return {
      key: `${FEED_KEY_PREFIX}${feed.provider}/${feed.product}`,
      name: feed.label,
      url: uri,
      source: `${OWM_SOURCE_NAME} · global, forecast at each asset`,
      pick: { kind: 'feed', uri },
    };
  });
  return [
    { label: BUILT_IN_FEEDS_GROUP_LABEL, items: builtIns },
    ...(feedItems.length > 0
      ? [{ label: OWM_GROUP_LABEL, items: feedItems }]
      : []),
    ...catalogGroups,
  ];
}

/** The option key a source selects, or null when it is not in the list. */
export function dataFeedKeyForSource(
  source: OrgEventAutomationSource | undefined
): string | null {
  const feed = parseFeedUri(source?.serviceUrl);
  if (feed) return `${FEED_KEY_PREFIX}${feed.provider}/${feed.product}`;
  const preset = feedPresetFromSource(source);
  if (FEED_OPTIONS.some((option) => option.value === preset)) {
    return `${PRESET_KEY_PREFIX}${preset}`;
  }
  const url = source?.serviceUrl?.trim();
  if (preset !== 'custom' || !url) return null;
  return findWeatherLayerByUrl(url)?.serviceUrl ?? null;
}

/** Points a condition at any FeatureServer/MapServer layer URL. */
export function applyFeedUrlToCondition(
  condition: OrgEventAutomationCondition,
  serviceUrl: string
): OrgEventAutomationCondition {
  const base = applyPresetToCondition(condition, 'custom');
  const previousLabel =
    condition.source?.preset === 'custom' ? condition.source.layerLabel : '';
  return {
    ...base,
    source: {
      ...sourceForPreset('custom'),
      serviceUrl,
      layerLabel:
        findWeatherLayerByUrl(serviceUrl)?.name ?? previousLabel ?? '',
      ...catalogSourceDefaults(serviceUrl),
    },
  };
}

/**
 * Points a condition at a non-ArcGIS feed. Each asset is its own feature, so
 * events open per asset. A field only survives when the feed is unchanged.
 */
export function applyExternalFeedToCondition(
  condition: OrgEventAutomationCondition,
  uri: string
): OrgEventAutomationCondition {
  const picked = parseFeedUri(uri);
  if (!picked) return condition;
  const current = parseFeedUri(condition.source?.serviceUrl);
  const sameFeed =
    current?.provider === picked.provider &&
    current?.product === picked.product;
  const serviceUrl = buildFeedUri({
    ...picked,
    units: current?.units ?? picked.units,
  });
  const source: OrgEventAutomationSource = {
    type: 'external_api',
    preset: 'custom',
    serviceUrl,
    occurrenceBy: 'asset',
    layerLabel: feedDisplayName(serviceUrl) ?? OWM_SOURCE_NAME,
  };
  if (condition.kind === 'attribute') {
    return sameFeed
      ? { ...condition, source }
      : { ...condition, source, field: '', operator: '>=', value: '' };
  }
  return { ...condition, source, geometryFrom: 'feature' };
}

/**
 * Switches a feed condition's unit system and converts its numeric threshold,
 * so "gust ≥ 80 km/h" becomes "gust ≥ 49.7 mph" and keeps its meaning.
 */
export function applyFeedUnitsToCondition(
  condition: OrgEventAutomationCondition,
  units: FeedUnits
): { condition: OrgEventAutomationCondition; converted: boolean } {
  const current = parseFeedUri(condition.source?.serviceUrl);
  if (!current || current.units === units || !condition.source) {
    return { condition, converted: false };
  }
  const serviceUrl = withFeedUnits(condition.source.serviceUrl, units);
  if (condition.kind !== 'attribute') {
    return {
      condition: { ...condition, source: { ...condition.source, serviceUrl } },
      converted: false,
    };
  }
  const nextField = feedFields(serviceUrl).find(
    (field) => field.name === condition.field
  );
  const snapshot = condition.source.fieldSnapshot;
  const source = {
    ...condition.source,
    serviceUrl,
    ...(snapshot && nextField
      ? { fieldSnapshot: { ...snapshot, alias: nextField.alias } }
      : {}),
  };
  const quantity = nextField?.quantity;
  const threshold = Number(condition.value);
  if (
    !quantity ||
    condition.value.trim() === '' ||
    !Number.isFinite(threshold)
  ) {
    return { condition: { ...condition, source }, converted: false };
  }
  const value = convertFeedValue(quantity, threshold, current.units, units);
  return {
    condition: { ...condition, source, value: String(value) },
    converted: true,
  };
}

export function applyDataFeedPick(
  condition: OrgEventAutomationCondition,
  pick: DataFeedPick
): OrgEventAutomationCondition {
  if (pick.kind === 'feed') {
    return applyExternalFeedToCondition(condition, pick.uri);
  }
  return pick.kind === 'preset'
    ? applyPresetToCondition(condition, pick.preset)
    : applyFeedUrlToCondition(condition, pick.serviceUrl);
}
